import React, { MouseEventHandler } from 'react'
import { useTranslation } from 'react-i18next'

import { Delete, Landscape } from '@mui/icons-material'
import { Badge, Box, Button, Card, CardActions, CardHeader, CardMedia, Chip, Divider, Typography } from '@mui/material'

import AddToCollectionButton from '../../../../components/AddToCollectionButton/AddToCollectionButton'
import { AnimatedImage } from '../../../../components/AnimatedImage/AnimatedImage'
import { FeatureGame } from '../../../game/types/Game'
import './ScreenshotCard.scss'

interface ScreenshotCardProps {
  title?: string
  inactive?: boolean
  badgeContent?: number
  imageUrl?: string
  onImageClick?: () => void
  showLatestBadge?: boolean
  onRemove?: (screenshotId: string) => void
  showAddToCollection?: boolean
  addToCollectionDisabledTooltip?: React.ReactNode
  screenshotId?: string
  game?: FeatureGame
  cardActionsChildren: React.ReactNode
  cardActionRibbon?: React.ReactNode
  relatedFeatures?: React.ReactNode
  onCardClick?: () => void
  onTitleClick?: () => void
}

const ScreenshotCard: React.FC<ScreenshotCardProps> = ({
  title,
  inactive,
  badgeContent,
  imageUrl,
  onImageClick,
  showLatestBadge,
  onRemove,
  showAddToCollection,
  addToCollectionDisabledTooltip,
  screenshotId,
  game,
  cardActionsChildren,
  cardActionRibbon,
  relatedFeatures,
  onCardClick,
  onTitleClick,
}) => {
  const { t } = useTranslation()

  const handleRemoveClick: MouseEventHandler = (evt) => {
    evt.stopPropagation()
    if (onRemove && screenshotId) {
      onRemove(screenshotId)
    }
  }

  return (
    <Badge className="ScreenshotCard__badge" color="primary" badgeContent={badgeContent && badgeContent > 0 ? badgeContent : 0}>
      <Card className="ScreenshotCard" elevation={3}>
        {title && (
          <CardHeader
            title={
              <>
                <span style={{ marginRight: inactive ? 8 : 0 }}>{title}</span>
                {inactive && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ verticalAlign: 'middle', mb: '3px' }} />}
              </>
            }
            onClick={onTitleClick}
          />
        )}
        <CardMedia className="ScreenshotCard__image" onClick={onImageClick}>
          {imageUrl ? (
            <AnimatedImage url={imageUrl} alt="" imgStyle={{ margin: '0 auto' }} className="ScreenshotCard__AnimatedImage_container" />
          ) : (
            <Box
              className="ScreenshotCard__image-placeholder"
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', gap: 1 }}
            >
              <Landscape sx={{ fontSize: 64, color: 'action.disabled' }} />
              <Typography variant="body2" color="text.secondary">
                {t('implementations:label_no_screenshots_available')}
              </Typography>
            </Box>
          )}
          {showLatestBadge && (
            <Typography component="span" className="ScreenshotCard__latest-badge">
              {t('common:latest')}
            </Typography>
          )}
          {showAddToCollection && screenshotId && game && (
            <AddToCollectionButton iconOnly={true} screenshotId={screenshotId} game={game} disabledTooltip={addToCollectionDisabledTooltip} />
          )}
          {onRemove && screenshotId && (
            <Button
              className="ScreenshotCard__button ScreenshotCard__button--remove"
              color="warning"
              variant="contained"
              startIcon={<Delete />}
              onClick={handleRemoveClick}
            />
          )}
        </CardMedia>
        <Box position="relative">
          {cardActionRibbon}
          <CardActions disableSpacing className="ScreenshotCard__actions">
            {cardActionsChildren}
          </CardActions>
        </Box>
        {relatedFeatures && (
          <Box className="ScreenshotCard__related-features" sx={{ px: 2, pt: 0, pb: 2 }} onClick={onCardClick}>
            <Divider sx={{ mb: 1 }} />
            {relatedFeatures}
          </Box>
        )}
      </Card>
    </Badge>
  )
}

export default ScreenshotCard
